/** biome-ignore-all lint/suspicious/noExplicitAny: <Allow exception> */
"use client";

import { useEffect, useMemo, useState } from "react";

type Order = "ASC" | "DSC";

type SortDataFunction<T> = (col: keyof T | string) => void;
type DeleteItemFunction = (id: number | string) => void;
type PaginateFunction = (page: number) => void;
type SearchFunction = (term: string) => void;

interface SortableTableHook<T> {
  tableData: T[];
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  sortData: SortDataFunction<T>;
  deleteItem: DeleteItemFunction;
  paginate: PaginateFunction;
  nextPage: () => void;
  prevPage: () => void;
  search: SearchFunction;
  startIndex: number;
  endIndex: number;
  totalData: number;
}

const useTable = <T extends Record<string, any>>(
  initialData: T[],
  initialItemsPerPage: number = 10
): SortableTableHook<T> => {
  const [tableData, setTableData] = useState<T[]>(initialData);
  const [order, setOrder] = useState<Order>("ASC");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(initialItemsPerPage);

  useEffect(() => {
    setTableData(initialData);
    setCurrentPage(1);
  }, [initialData]);

  const sortData: SortDataFunction<T> = (col) => {
    const colString = String(col);

    const [parentCol, childCol] = colString.split(".");

    if (order === "ASC") {
      const sorted = [...tableData].sort((a, b) => {
        const aValue = childCol ? a[parentCol][childCol] : a[colString];
        const bValue = childCol ? b[parentCol][childCol] : b[colString];

        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.toLowerCase() > bValue.toLowerCase() ? 1 : -1;
        } else {
          return aValue > bValue ? 1 : -1;
        }
      });

      setTableData(sorted);
      setOrder("DSC");
    } else {
      const sorted = [...tableData].sort((a, b) => {
        const aValue = childCol ? a[parentCol][childCol] : a[colString];
        const bValue = childCol ? b[parentCol][childCol] : b[colString];

        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.toLowerCase() < bValue.toLowerCase() ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setTableData(sorted);
      setOrder("ASC");
    }
  };

  const deleteItem: DeleteItemFunction = (id) => {
    const updatedData = tableData.filter((item) => item.id !== id);
    setTableData(updatedData);
  };

  const paginate: PaginateFunction = (page) => {
    setCurrentPage(page);
  };
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };
  const search: SearchFunction = (term) => {
    const filteredData = initialData.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(term.toLowerCase())
      )
    );
    setTableData(filteredData);
    setCurrentPage(1);
  };

  const { currentItems, totalPages, startIndex, endIndex } = useMemo(() => {
    const totalItems = tableData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const start = (currentPage - 1) * itemsPerPage;
    const end = Math.min(start + itemsPerPage - 1, totalItems - 1);
    const items = tableData.slice(start, start + itemsPerPage);

    return {
      currentItems: items,
      totalPages,
      startIndex: start,
      endIndex: end
    };
  }, [tableData, currentPage, itemsPerPage]);

  return {
    tableData: currentItems,
    currentPage,
    itemsPerPage,
    totalPages,
    sortData,
    deleteItem,
    paginate,
    search,
    nextPage,
    prevPage,
    startIndex,
    endIndex,
    totalData: tableData.length
  };
};

export default useTable;
